import { useTableControls, TableColumn } from '../../hooks/useTableControls'
import ColumnVisibilityButton from '../ColumnVisibilityButton/ColumnVisibilityButton'
import TableToolbar from '../TableToolbar/TableToolbar'
import { Close as CloseIcon } from '@mui/icons-material'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'

interface CityInfo {
  readonly [key: string]: number | string
  readonly name: string
  readonly country: string
  readonly province: string
}

interface CityAccessDialogProps {
  readonly userName: string
  readonly cities: CityInfo[]
  readonly onClose: () => void
}

const CityAccessDialog = ({
  userName,
  cities,
  onClose,
}: CityAccessDialogProps) => {
  const defaultColumns: TableColumn[] = useMemo(
    () => [
      { id: 'name', label: 'City', visible: true, sortable: true },
      {
        id: 'province',
        label: 'Province/State',
        visible: true,
        sortable: true,
      },
      { id: 'country', label: 'Country', visible: true, sortable: true },
    ],
    []
  )

  const {
    page,
    rowsPerPage,
    columns,
    visibleColumns,
    paginatedData,
    sortBy,
    sortDirection,
    handleChangePage,
    handleChangeRowsPerPage,
    toggleColumn,
    handleSort,
  } = useTableControls(cities, defaultColumns)

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#31353D', backgroundImage: 'none' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            {userName}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              City Access ({cities.length} cities)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ColumnVisibilityButton
              columns={columns}
              onToggleColumn={toggleColumn}
            />
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDirection : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((city: CityInfo, index: number) => (
                <TableRow key={`${city.name}-${index}`}>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.id}>{city[column.id]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TableToolbar
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={cities.length}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </DialogContent>
    </Dialog>
  )
}

export default CityAccessDialog
